import { useRef, useEffect, MutableRefObject } from 'react';
import { NavLink, Link } from 'react-router-dom';
import './Header.scss';
import logo from '../../assets/media-manager-logo.webp';
import { RootState } from '../../app/store';
import { logout } from '../../app/slices/authenticationSlice';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import Swal from 'sweetalert2';
import { mediaManagerServerApi } from '../../app/services/mediaManagerServerApi';
import { NavMenu } from './NavMenu';
import { UserMenu } from './UserMenu';
import { isAdminToken } from '../../shared/currentUser';
import { resetMovieState } from '../../app/slices/addMediaSlice';
import { resetTvShowState } from '../../app/slices/addTvShowSlice';
import { resetSeasonState } from '../../app/slices/addTvShowSeasonSlice';
import { resetEpisodeState } from '../../app/slices/addTvShowSeasonEpisodeSlice';
import { resetAutographOrderState } from '../../app/slices/addAutographOrderSlice';
import { resetAutographState } from '../../app/slices/addAutographSlice';

const navLinks = [
  {
    display: 'Home',
    path: '/',
  },
  {
    display: 'Movies',
    path: '/movies',
  },
  {
    display: 'TV Shows',
    path: '/tv-shows',
  },
  {
    display: 'Autographs',
    path: '/autographs',
  },
];

// Worth reaching, but not what the app is about - the nav itself stays
// the three catalogues. Artists is already a page; it simply had no way
// in.
const moreLinks = [
  {
    display: 'Artists',
    path: '/artists',
    icon: 'bi-people',
  },
  {
    display: 'TV mode',
    path: '/tv-mode',
    icon: 'bi-tv',
  },
];

// Add Media had no way in at all - it was reachable only by typing the
// address. It belongs here rather than in the nav proper for the same
// reason as the rest of this menu, and it is only offered to somebody
// who can actually use it: every step of that wizard is superuser-only
// server-side, so showing it to anyone else would be offering a door
// onto a 403.
const ADD_MEDIA_PATH = '/add-media';

const adminLinks = [
  {
    display: 'Add Media',
    path: ADD_MEDIA_PATH,
    icon: 'bi-plus-square',
  },
];

export const Header = () => {
  const headerRef = useRef() as MutableRefObject<HTMLDivElement>;
  const dispatch = useAppDispatch();

  // Add Media from this menu is always a fresh start.
  //
  // The page shows its six kinds of media only while nothing is being
  // added, and each wizard keeps where it had got to - and the half
  // filled form that goes with it - in the store. Nothing cleared any
  // of that on the way out, so coming back here landed on whichever
  // wizard was last open, at the step it was left at, and there was no
  // way back to the screen the menu item names.
  //
  // Only from here. The wizards reach each other in place, without
  // going through this link: a season offers to add its episodes, and
  // a new file hands its title to the movie or episode wizard. Those
  // are carrying work forward and must not be swept up in this.
  const startAddMediaAfresh = () => {
    dispatch(resetMovieState());
    dispatch(resetTvShowState());
    dispatch(resetSeasonState());
    dispatch(resetEpisodeState());
    dispatch(resetAutographOrderState());
    dispatch(resetAutographState());
  };

  useEffect(() => {
    const shrinkHeader = () => {
      if (
        document.body.scrollTop > 100 ||
        document.documentElement.scrollTop > 100
      ) {
        headerRef.current.classList.add('shrink');
      } else {
        headerRef.current.classList.remove('shrink');
      }
    };
    window.addEventListener('scroll', shrinkHeader);
    return () => {
      window.removeEventListener('scroll', shrinkHeader);
    };
  }, []);

  const userAccessToken = useAppSelector(
    (state: RootState) => state.authentication.userAccessToken,
  );
  const logoutUser = () => {
    dispatch(logout());
    dispatch(mediaManagerServerApi.util.resetApiState());
    Swal.fire({
      title: 'Logged out succesfully',
      text: 'Redirecting to login page',
      icon: 'success',
      timer: 1000,
      showConfirmButton: false,
    });
  };

  return (
    <div ref={headerRef} className="media-header">
      <div className="media-header__wrap container">
        <div className="media-logo-header">
          <img src={logo} alt="media-manager-logo" />
          <Link className="media-a" to="/">
            MediaManager
          </Link>
        </div>
        {userAccessToken && (
          <ul className="media-header__nav">
            {navLinks.map((navLink) => (
              <NavLink
                to={navLink.path}
                className={({ isActive }) =>
                  isActive ? 'media-a navlink active' : 'media-a navlink'
                }
                key={navLink.path}
              >
                {navLink.display}
              </NavLink>
            ))}
            <NavMenu
              label="More"
              items={[
                ...moreLinks,
                ...(isAdminToken(userAccessToken) ? adminLinks : []),
              ].map((link) => ({
                to: link.path,
                label: link.display,
                icon: link.icon,
                onSelect:
                  link.path === ADD_MEDIA_PATH
                    ? startAddMediaAfresh
                    : undefined,
              }))}
            />
            <UserMenu token={userAccessToken} onLogout={logoutUser} />
          </ul>
        )}
      </div>
    </div>
  );
};
